import React from 'react';
import { Mail, Link2 } from 'lucide-react';
import { MessageFormatBar } from '../ui/MessageFormatBar';
import { DestinataireEmail } from '../ui/DestinataireEmail';
import { TokenPicker, TokenDef } from '../ui/TokenPicker';

const JETONS: TokenDef[] = [
    { token: '{{prenom}}', desc: 'Prénom du client' },
    { token: '{{nom}}', desc: 'Nom (ou raison sociale) du client' },
    { token: '{{montant_devis}}', desc: 'Montant TTC du devis' },
    { token: '{{entreprise}}', desc: 'Votre établissement' },
    { token: '{{logo}}', desc: 'Votre logo' },
    { token: '{{bouton}}', desc: 'Bouton vers la page de signature (sinon ajouté en fin de message)' },
];

export interface DevisEmail {
    send_email: boolean;
    email_subject: string;
    email_message: string;
}

/**
 * Le message qui accompagne le devis, et son lien de signature.
 *
 * Contrairement à la plaquette, il n'y a rien à choisir ici : le bouton mène toujours à la page de
 * signature de CE devis. Le client y retélécharge le document, y dépose son exemplaire signé, ou y dit
 * qu'il ne donne pas suite — et le dossier cesse d'attendre pour rien.
 *
 * Objet et message vides = les modèles enregistrés dans Paramètres > Notifications. C'est le comportement
 * normal : on ne réécrit pas son email à chaque devis. Les remplir ici ne sert qu'au coup par coup.
 */
export function DevisEmailFields({ valeur, onChange, destinataire, destinataireEstReferent }: {
    valeur: DevisEmail;
    onChange: (v: DevisEmail) => void;
    /** À qui le mail partira réellement — le référent prime sur l'entreprise (cf. DestinataireEmail). */
    destinataire?: string;
    destinataireEstReferent?: boolean;
}) {
    const messageRef = React.useRef<HTMLTextAreaElement>(null);
    const modifier = (patch: Partial<DevisEmail>) => onChange({ ...valeur, ...patch });

    return (
        <div className="space-y-3 border-t border-border pt-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
                <input type="checkbox" checked={valeur.send_email} onChange={(e) => modifier({ send_email: e.target.checked })}
                    className="accent-brand-600 w-4 h-4 mt-0.5" />
                <span>
                    <span className="text-xs font-bold text-content flex items-center gap-1.5"><Mail size={13} /> Envoyer le devis au client par email</span>
                    <span className="block text-[11px] text-content-muted mt-0.5">
                        Sans cette case, le devis est seulement conservé sur le dossier — et il faudra
                        téléverser vous-même l'exemplaire signé quand il reviendra.
                    </span>
                </span>
            </label>

            {valeur.send_email && (
                <div className="space-y-3 pl-1">
                    <DestinataireEmail email={destinataire} estReferent={destinataireEstReferent} />
                    <p className="text-[11px] text-content-muted leading-relaxed p-2.5 rounded-xl bg-surface-soft border border-border flex items-start gap-1.5">
                        <Link2 size={12} className="mt-0.5 shrink-0" />
                        Le devis part en pièce jointe, avec un bouton vers sa page de signature : le client
                        y dépose son exemplaire signé, ou vous dit qu'il ne donne pas suite. Vous êtes
                        prévenu dans les deux cas.
                    </p>

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Objet</label>
                        <input value={valeur.email_subject} onChange={(e) => modifier({ email_subject: e.target.value })}
                            placeholder="Laissez vide pour votre modèle enregistré"
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-1.5 text-sm text-content outline-none focus:border-brand-500" />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Message</label>
                        <MessageFormatBar textareaRef={messageRef} value={valeur.email_message} onChange={(v) => modifier({ email_message: v })} />
                        <textarea ref={messageRef} value={valeur.email_message} rows={6}
                            onChange={(e) => modifier({ email_message: e.target.value })}
                            placeholder={"Laissez vide pour votre modèle enregistré (Paramètres > Notifications)."}
                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500 resize-y" />
                        <TokenPicker tokens={JETONS} textareaRef={messageRef} value={valeur.email_message} onChange={(v) => modifier({ email_message: v })} />
                    </div>
                </div>
            )}
        </div>
    );
}
